import { memo, useCallback, useState } from "react";
import { clsx } from "clsx";

import { useUser } from "@/entities/Auth";
import { useAppDispatch, useAppSelector } from "@/shared/lib/hooks/redux";
import { Theme, useTheme } from "@/shared/lib/hooks/useTheme";
import { Dialog } from "@/shared/ui/Dialog";
import { Title } from "@/shared/ui/Title";

import { getIsLoading } from "../../model/selectors/subscriptionSelectors";
import { createSubscription } from "../../model/services/createSubscription";
import type { ProductData } from "../../model/types/productData";
import { BillingSelection } from "../BillingSelection/BillingSelection";
import { PlanSelection } from "../PlanSelection/PlanSelection";

import cls from "./Canceled.module.scss";

enum Step {
  Plan = "plan",
  Billing = "billing",
}

interface CanceledProps {
  className?: string;
  onUpdate: (reactivationValue: number) => void;
}

export const Canceled = memo(({ className, onUpdate }: CanceledProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useUser();
  const { theme } = useTheme();

  const isLoading = useAppSelector(getIsLoading);

  const [step, setStep] = useState(Step.Plan);
  const [stripePriceId, setStripePriceId] = useState<string>();
  const [selectedProduct, setSelectedProduct] = useState<ProductData>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const variant = theme === Theme.Dark ? "dark" : "light";

  const onCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onSelectPlan = useCallback(() => {
    setStep(Step.Billing);
  }, []);

  const onBackToPlans = useCallback(() => {
    setStep(Step.Plan);
  }, []);

  const onSelectBilling = useCallback((product: ProductData) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  }, []);

  const onSubmit = useCallback(async () => {
    if (isLoading || !selectedProduct) {
      return;
    }

    const result = await dispatch(createSubscription({ stripePriceId: selectedProduct.stripePriceId }));

    if (result.meta.requestStatus === "fulfilled") {
      onUpdate(Number(selectedProduct.price));
    }
  }, [onUpdate, dispatch, isLoading, selectedProduct]);

  return (
    <div className={clsx(cls.Canceled, className)}>
      <div className={cls.Content}>
        <Title>{step === Step.Plan ? "Choose the plan that’s right for you" : "Choose Your Billing"}</Title>
        <p>
          {step === Step.Plan
            ? "Reactivate your plan to regain access to unlimited Tarot readings and intuitive guidance."
            : "Get 2 months FREE when you choose annual."}
        </p>
        {step === Step.Plan ? (
          <PlanSelection
            variant={variant}
            stripePriceId={stripePriceId}
            preferredStripePriceId={currentUser.product?.stripePriceId}
            onChange={setStripePriceId}
            onSubmit={onSelectPlan}
          />
        ) : (
          <BillingSelection
            optionsClassName={cls.Block}
            variant={variant}
            submitText="REACTIVATE"
            showSelectedProduct={false}
            isBusy={isLoading}
            stripePriceId={stripePriceId}
            onSubmit={onSelectBilling}
            onBack={onBackToPlans}
          />
        )}
      </div>
      <Dialog
        isOpen={isModalOpen}
        isBusy={isLoading}
        onClose={onCloseModal}
        title="Are you sure you want to reactivate your plan?"
        submit={{
          text: "Yes",
          onClick: async () => {
            await onSubmit();
            onCloseModal();
          },
        }}
        cancel={{ text: "No" }}
      />
    </div>
  );
});
